import { useEffect, useState } from "react";
import { PopulationService } from "@services/engagements/population/populationService";
import { ConsumerJourney } from "@models/population.model";

/* The journey can be addressed two ways (matching the two endpoints): by
   campaign — the launch-scoped population panel — or by population — the
   data-file roster, which exists before and apart from any campaign
   (MPN-1602). Exactly one scope is given. */
export type JourneyScope =
  | { launchId: number | null; populationSegmentId?: undefined }
  | { populationSegmentId: number | null; launchId?: undefined };

type UseConsumerJourneyArgs = JourneyScope & {
  enabled: boolean;
  consumerId: number | null;
};

interface UseConsumerJourneyResult {
  data: ConsumerJourney | null;
  loading: boolean;
  error: string | null;
}

export function useConsumerJourney({
  enabled,
  launchId,
  populationSegmentId,
  consumerId,
}: UseConsumerJourneyArgs): UseConsumerJourneyResult {
  const [data, setData] = useState<ConsumerJourney | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const scopeId = launchId ?? populationSegmentId ?? null;
    if (!enabled || scopeId === null || consumerId === null) return;

    let cancelled = false;
    setData(null);
    setLoading(true);
    setError(null);

    const service = new PopulationService();
    const request =
      launchId != null
        ? service.getConsumerJourney(launchId, consumerId)
        : service.getSegmentConsumerJourney(scopeId, consumerId);

    request
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load consumer journey");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, launchId, populationSegmentId, consumerId]);

  return { data, loading, error };
}
